import PercentIcon from "@mui/icons-material/Percent";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import type { AddAdvanceOrProformaInvoiceRequestBody } from "../../../api/api";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import FormCheckbox from "../../components/formComponents/FormCheckbox";
import FormDatePicker from "../../components/formComponents/FormDatePicker";
import FormNumberField from "../../components/formComponents/FormNumberField";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { Price } from "../../misc/price";
import type { OrderPriceByDepartmentResponse } from "../ordersApi";
import InvoiceSellerSelect from "./InvoiceSellerSelect";
import type { SellerSelection } from "./invoiceSellerUtils";
import { AUTOMATIC_SELLER_SELECTION, resolveDepartmentForSeller } from "./invoiceSellerUtils";
import PriceByDepartmentDialog from "./PriceByDepartmentDialog";
import { useOrderPriceByDepartment } from "./useOrderPriceByDepartment";

interface Fill90PercentButtonProps {
  readonly limits: OrderPriceByDepartmentResponse | undefined;
  onFill: (amount: number) => void;
}

const Fill90PercentButton: FC<Fill90PercentButtonProps> = ({ limits, onFill }) => {
  const filledAmount = useMemo(() => {
    const availableTotal = Object.values(limits?.proforma ?? {}).reduce(
      (sum, { availableAmount }) => sum + (availableAmount ?? 0),
      0,
    );
    return availableTotal > 0 ? (new Price(availableTotal).multiply(0.9).round(0).value as number) : null;
  }, [limits]);

  if (filledAmount === null) {
    return null;
  }

  return (
    <Tooltip title="Wypełnij 90% kwoty do wystawienia proformy">
      <IconButton aria-label="Wypełnij 90% kwoty do wystawienia proformy" onClick={() => onFill(filledAmount)}>
        <PercentIcon />
      </IconButton>
    </Tooltip>
  );
};

interface Props {
  readonly open: boolean;
  readonly orderId: string;
  readonly currency: Currency;
  readonly type: "ADVANCE" | "PROFORMA";
  readonly defaultAmount?: number;
  onClose: () => void;
  onSubmit: (request: AddAdvanceOrProformaInvoiceRequestBody) => Promise<void>;
}

interface FormState {
  readonly value: number;
  readonly paymentDays: number;
  readonly paid: boolean;
  readonly sendByEmail: boolean;
  readonly includeOrderNameInDescription: boolean;
  readonly paymentDate?: Dayjs;
  readonly seller: SellerSelection;
}

const defaultFormValues = (defaultAmount?: number): FormState => ({
  value: defaultAmount ?? 0,
  paymentDays: 7,
  paid: false,
  sendByEmail: true,
  includeOrderNameInDescription: false,
  paymentDate: dayjs().subtract(1, "day"),
  seller: AUTOMATIC_SELLER_SELECTION,
});

function formatAvailableAmount(amount: number, currency: Currency): string {
  return new Price(amount).round(2).toDisplayValue(currency);
}

const AdvanceOrProformaInvoiceDialog: FC<Props> = ({
  open,
  orderId,
  currency,
  type,
  defaultAmount,
  onClose,
  onSubmit,
}) => {
  const { control, handleSubmit, watch, reset, trigger, formState, setValue } = useForm<FormState>({
    defaultValues: defaultFormValues(defaultAmount),
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) {
      reset(defaultFormValues(defaultAmount));
    }
  }, [open, defaultAmount, reset]);

  const paidChecked = watch("paid");
  const watchedValue = watch("value");
  const watchedSeller = watch("seller");

  const { data: limits } = useOrderPriceByDepartment(orderId);
  const invoiceKindKey = type === "ADVANCE" ? "advance" : "proforma";
  const kindLimits = limits?.[invoiceKindKey] ?? {};
  const availableSellers = limits?.sellerAssignmentOrder.filter((id) => !!kindLimits[id]) ?? [];

  const sellerRules = useMemo(
    () => ({
      validate: (sellerValue: string | number, formValues: FormState) => {
        const seller = sellerValue as SellerSelection;
        if (seller === AUTOMATIC_SELLER_SELECTION) {
          return true;
        }
        if (!limits) {
          return true;
        }
        const sellerLimits = kindLimits[seller];
        if (!sellerLimits) {
          return true;
        }
        const invoiceValue = formValues.value;
        if (invoiceValue > sellerLimits.availableAmount) {
          return `Kwota przekracza dostępną kwotę dla sprzedawcy ${sellerLimits.seller.shortName} (${formatAvailableAmount(sellerLimits.availableAmount, currency)})`;
        }
        if (resolveDepartmentForSeller(seller, invoiceValue, invoiceKindKey, limits) === undefined) {
          return "Kwota nie mieści się w limicie żadnego działu tego sprzedawcy — wybierz mniejszą kwotę lub automatyczny wybór.";
        }
        return true;
      },
    }),
    [limits, kindLimits, invoiceKindKey, currency],
  );

  useEffect(() => {
    if (availableSellers.length > 0) {
      void trigger("seller");
    }
  }, [watchedValue, watchedSeller, limits, availableSellers.length, trigger]);

  const title = type === "PROFORMA" ? "Faktura proforma" : "Faktura zaliczkowa";

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {title}
        <PriceByDepartmentDialog orderId={orderId} />
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <FormNumberField
              name="value"
              control={control}
              label="Kwota"
              suffix={currency}
              margin="dense"
              rules={{
                required: { value: true, message: "Kwota nie może być pusta" },
                min: { value: 0.01, message: "Kwota musi być większa od zera" },
              }}
            />
          </Box>
          {type === "PROFORMA" && (
            <Fill90PercentButton
              limits={limits}
              onFill={(amount) => setValue("value", amount, { shouldValidate: true })}
            />
          )}
        </Stack>
        <FormNumberField
          name="paymentDays"
          control={control}
          label="Termin płatności"
          suffix="dni"
          margin="dense"
          rules={{
            required: { value: true, message: "Termin płatności nie może być pusty" },
            min: { value: 1, message: "Liczba dni musi być większa od zera" },
          }}
        />
        <InvoiceSellerSelect
          orderId={orderId}
          currency={currency}
          invoiceKind={invoiceKindKey}
          control={control}
          name="seller"
          rules={sellerRules}
        />
        <Grid container>
          <Grid size={12}>
            <FormCheckbox name="paid" control={control} label="Opłacona" />
          </Grid>
          <Grid size={12}>
            <FormCheckbox name="sendByEmail" control={control} label="Wyślij mailem do klienta" />
          </Grid>
          <Grid size={12}>
            <FormCheckbox
              name="includeOrderNameInDescription"
              control={control}
              label="Dołącz nazwę zamówienia do opisu faktury"
            />
          </Grid>
        </Grid>
        {paidChecked && (
          <Grid size={12}>
            <FormDatePicker control={control} name="paymentDate" label="Data opłacenia" fullWidth />
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <CancelButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
        <AsyncLoadingButton
          disabled={!!formState.errors.seller}
          onClick={handleSubmit((s) => {
            const department =
              s.seller !== AUTOMATIC_SELLER_SELECTION && limits
                ? resolveDepartmentForSeller(s.seller, s.value, invoiceKindKey, limits)
                : undefined;
            return onSubmit({
              value: s.value,
              paid: s.paid,
              paymentDays: s.paymentDays,
              sendByEmail: s.sendByEmail,
              includeOrderNameInDescription: s.includeOrderNameInDescription,
              paymentDate: s.paid ? (s.paymentDate?.format(ISO_DATE_FORMAT) ?? "") : undefined,
              department,
            } as AddAdvanceOrProformaInvoiceRequestBody);
          })}
        >
          Utwórz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdvanceOrProformaInvoiceDialog;
